/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import Blog from '../../features/blog/components/Blog';
import {
  getContentStackBlogDataFromCache,
  prefetchContentStackBlogData,
} from '@/features/blog/api/getContentStackBlogData';
import { type PageContextType } from '@/next-types';
import { configureSharedPageData } from '@/utils/configureSharedPageData.server';
import { dehydrate } from '@tanstack/react-query';
import { prefetchContentStackBlogMenuNavigationData } from '@/features/blog/api/getContentStackBlogMenuNavigationData';
import { prefetchContentStackRelatedBlogsData } from '@/features/blog/api/getContentStackRelatedBlogsData';
import { blogPrefix, isValidBlogLocale } from '@/features/blog/utils/isValidBlogLocale';
import { getBlogPath } from '@/features/blog/utils/getBlogPath';
import { isServer } from '@/utils/isServer';
import { getGlobalStateQueryData } from '@/hooks/useGlobalState';

function BlogPage() {
  return <Blog />;
}

BlogPage.getInitialProps = async (context: PageContextType) => {
  if (isServer) {
    const { queryClient, initialAppState } = await configureSharedPageData(context);
    const appData = getGlobalStateQueryData('appData', queryClient) ?? { deviceType: '' };
    const isBot = appData.deviceType === 'bot';
    const locale = initialAppState.locale;

    if (!isValidBlogLocale(context.asPath, locale)) {
      const { res } = context;
      res?.writeHead(302, { Location: '/' }).end();
      return {};
    }

    const { query, req } = context;
    const blogRoot = blogPrefix[locale];
    const { fullPath: blogPath } = getBlogPath(locale, query, context.pathname);

    await prefetchContentStackBlogData({ queryClient, blogPath, req, query: context.query });
    const blogData = getContentStackBlogDataFromCache({ queryClient, blogPath });

    if (!blogData) {
      const { res } = context;
      res?.writeHead(302, { Location: blogRoot }).end();
      return {};
    }

    const blogMenuPromise = prefetchContentStackBlogMenuNavigationData({ queryClient, context });
    const primaryCategory = blogData.blogContent.post_content_category?.primary_category;
    const currentBlogUrl = blogData.blogContent.url;
    const postType: string | undefined = blogData.blogContent.post_type;

    const blogRelatedBlogsPromise =
      isBot && primaryCategory && currentBlogUrl && postType
        ? prefetchContentStackRelatedBlogsData({
            queryClient,
            params: {
              primaryCategory,
              currentBlogUrl,
              postType,
            },
            req,
          })
        : null;

    await Promise.all([blogMenuPromise, blogRelatedBlogsPromise]);

    return {
      dehydratedState: dehydrate(queryClient),
      initialAppState,
    };
  }
  return {};
};

export default BlogPage;
